import { isValidElement, type ReactNode } from 'react'
import { toast, type Id, type ToastItem } from 'react-toastify'

export type ActiveErrorToast = {
  id: Id
  text: string
}

type Listener = (toasts: ActiveErrorToast[]) => void

const listeners = new Set<Listener>()
const activeToastMap = new Map<Id, ActiveErrorToast>()

const notify = () => {
  const snapshot = Array.from(activeToastMap.values())
  listeners.forEach(listener => listener(snapshot))
}

const extractDetailsFromData = (data: unknown): string | null => {
  if (!data) return null

  if (typeof data === 'string' || typeof data === 'number') {
    return String(data)
  }

  if (Array.isArray(data)) {
    const flattened = data
      .map(item => {
        if (typeof item === 'string' || typeof item === 'number') {
          return String(item)
        }
        if (item && typeof item === 'object' && 'message' in item && typeof item.message === 'string') {
          return item.message
        }
        return null
      })
      .filter(Boolean)
      .join(', ')
    return flattened || null
  }

  if (typeof data === 'object') {
    const message = (data as { message?: unknown }).message
    if (typeof message === 'string') {
      return message
    }

    if ('errors' in (data as any) && Array.isArray((data as any).errors)) {
      const combined = (data as any).errors
        .map((err: unknown) => {
          if (typeof err === 'string') return err
          if (err && typeof err === 'object' && 'message' in err && typeof err.message === 'string') {
            return err.message
          }
          return null
        })
        .filter(Boolean)
        .join(', ')
      if (combined) {
        return combined
      }
    }

    try {
      return JSON.stringify(data)
    } catch {
      return null
    }
  }

  return null
}

const extractTextFromChildren = (children: ReactNode): string | null => {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children)
  }

  if (Array.isArray(children)) {
    const combined = children
      .map(child => (typeof child === 'string' || typeof child === 'number' ? String(child) : null))
      .filter(Boolean)
      .join(' ')
    return combined || null
  }

  return null
}

const parseToastText = (toastItem: ToastItem): string | null => {
  const { content, data } = toastItem

  if (typeof content === 'string' || typeof content === 'number') {
    return String(content)
  }

  if (isValidElement(content)) {
    const messageProp = typeof content.props?.message === 'string' ? content.props.message : null
    const detailSource = content.props?.data ?? data
    const details = extractDetailsFromData(detailSource)

    if (messageProp) {
      return details ? `${messageProp}\n${details}` : messageProp
    }

    const textFromChildren = extractTextFromChildren(content.props?.children)
    if (textFromChildren) {
      return textFromChildren
    }
  }

  return extractDetailsFromData(data)
}

const handleToastChange = (toastItem: ToastItem) => {
  if (toastItem.status === 'removed' || toastItem.type !== 'error') {
    if (activeToastMap.delete(toastItem.id)) {
      notify()
    }
    return
  }

  const text = parseToastText(toastItem)
  if (!text) return

  activeToastMap.set(toastItem.id, { id: toastItem.id, text })
  notify()
}

let isTracking = false

const startTracking = () => {
  if (isTracking || typeof window === 'undefined') {
    return
  }
  toast.onChange(handleToastChange)
  isTracking = true
}

startTracking()

export const getActiveErrorToasts = (): ActiveErrorToast[] => Array.from(activeToastMap.values())

export const subscribeToActiveErrorToasts = (listener: Listener) => {
  startTracking()
  listeners.add(listener)
  listener(getActiveErrorToasts())

  return () => {
    listeners.delete(listener)
  }
}

export const clearActiveErrorToasts = () => {
  if (activeToastMap.size === 0) return
  activeToastMap.clear()
  notify()
}
